import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import utc from 'dayjs/plugin/utc';
import {
    ArrowLeftRight,
    Download,
    Eye,
    FileText,
    Pencil,
    Search,
    Send,
    Trash2,
    X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { httpClient } from '../../common/httpclient';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DialogForm from '../../component/dialog/DialogForm';
import QuoteWhatsappDialog from '../../component/dialog/QuoteWhatsappDialog';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import StatusChip from '../../component/common/StatusChip';
import NewItemButton from '../../component/common/NewItemButton';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileQuotesList from '../../component/servicequote/MobileQuotesList';
import useListFiltersSnapshot, { formatSnapshotDate, parseSnapshotDate } from '../../hooks/useListFiltersSnapshot';

dayjs.extend(utc);

// Mesmos 5 status reais de orçamento de serviço usados por `StatusChip
// domain="serviceQuote"` (DESIGN-SYSTEM.md §4) — a API retorna "ordem
// gerada", mas o rótulo exibido (aqui e no chip removível) é sempre
// "Convertido".
const STATUS_OPTIONS = ['novo', 'enviado para cliente', 'aguardando aprovacao', 'aprovado', 'ordem gerada'];

const STATUS_LABELS = {
    'novo': 'Novo',
    'enviado para cliente': 'Enviado',
    'aguardando aprovacao': 'Aguardando Aprovação',
    'aprovado': 'Aprovado',
    'ordem gerada': 'Ordem Gerada',
};

function serviceQuoteStatusLabel(value) {
    return STATUS_LABELS[value?.toLowerCase()] || value;
}

const PAGE_SIZE_OPTIONS = [5, 8, 15, 25, 50];

// Snapshot dos filtros aplicados (sessionStorage, por empresa), gravado SOMENTE
// ao abrir o detalhe de um orçamento e restaurado só na volta (POP). Ver
// `hooks/useListFiltersSnapshot.js`. Removido também no logout (AccountMenu).
export const QUOTES_FILTERS_STORAGE_PREFIX = 'service_quotes_list_filters:';

const EMPTY_QUOTES_FILTERS = { start: null, end: null, status: '', customer: '', search: '', page: 0, pageSize: 8 };

function sanitizeQuotesFilters(saved) {
    return {
        start: parseSnapshotDate(saved.start),
        end: parseSnapshotDate(saved.end),
        status: STATUS_OPTIONS.includes(saved.status) ? saved.status : '',
        customer: typeof saved.customer === 'string' ? saved.customer : '',
        search: typeof saved.search === 'string' ? saved.search : '',
        page: Number.isInteger(saved.page) && saved.page >= 0 ? saved.page : 0,
        pageSize: PAGE_SIZE_OPTIONS.includes(saved.pageSize) ? saved.pageSize : 8,
    };
}

export default function List() {
    const { initialFilters, saveSnapshot } = useListFiltersSnapshot(
        QUOTES_FILTERS_STORAGE_PREFIX,
        EMPTY_QUOTES_FILTERS,
        sanitizeQuotesFilters,
    );
    const [search, setSearch] = useState(initialFilters.search);
    // Busca (número ou cliente) com debounce — evita uma requisição por tecla.
    const [debouncedSearch, setDebouncedSearch] = useState(initialFilters.search.trim());
    const previousDebouncedSearch = useRef(initialFilters.search.trim());
    const [loading, setLoading] = useState(true);
    // Filtro de data inicia vazio: sem datas, traz todos os orçamentos (mais
    // recentes primeiro), paginados no servidor.
    const [selectedDate, setSelectedDate] = useState(initialFilters.start);
    const [selectedDateEnd, setSelectedDateEnd] = useState(initialFilters.end);
    // Período efetivamente aplicado na última busca — só muda em "Buscar"/"Limpar".
    const [appliedPeriod, setAppliedPeriod] = useState({ start: initialFilters.start, end: initialFilters.end });
    const [quotes, setQuotes] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState(initialFilters.status);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(initialFilters.customer);
    // Paginação server-side (page é 0-based na tela; a API usa 1-based).
    const [page, setPage] = useState(initialFilters.page);
    const [pageSize, setPageSize] = useState(initialFilters.pageSize);
    const [totalCount, setTotalCount] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');
    const [printingQuoteId, setPrintingQuoteId] = useState(null);
    const [openConvertDialog, setOpenConvertDialog] = useState(false);
    const [quoteToConvert, setQuoteToConvert] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [quoteToDelete, setQuoteToDelete] = useState(null);
    const [quoteToWhatsapp, setQuoteToWhatsapp] = useState(null);
    const isMobileShell = useIsMobileShell();
    // Mobile: lista contínua ("Carregar mais") em vez de paginação; página própria para não
    // interferir na paginação do desktop/tablet.
    const MOBILE_PAGE_SIZE = 20;
    const [mobilePage, setMobilePage] = useState(0);

    // Debounce da busca; ao mudar o termo volta para a primeira página.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(timer);
    }, [search]);

    // Só volta para a primeira página se o termo realmente mudou (na montagem
    // o termo restaurado não deve zerar a página restaurada).
    useEffect(() => {
        if (previousDebouncedSearch.current === debouncedSearch) return;
        previousDebouncedSearch.current = debouncedSearch;
        setPage(0);
        setMobilePage(0);
    }, [debouncedSearch]);

    // Busca a página atual no servidor sempre que filtros/página/tamanho mudam.
    useEffect(() => {
        let cancelled = false;
        const requestPage = isMobileShell ? mobilePage : page;
        const params = { page: requestPage + 1, page_size: isMobileShell ? MOBILE_PAGE_SIZE : pageSize };
        if (appliedPeriod.start && appliedPeriod.start.isValid()) params.startDate = appliedPeriod.start.format('DD/MM/YYYY');
        if (appliedPeriod.end && appliedPeriod.end.isValid()) params.endDate = appliedPeriod.end.format('DD/MM/YYYY');
        if (selectedStatus) params.status = selectedStatus;
        if (selectedCustomer) params.customerId = selectedCustomer;
        if (debouncedSearch) params.search = debouncedSearch;

        setLoading(true);
        httpClient.get('/servicequotes', { params })
            .then((response) => {
                if (cancelled) return;
                const rows = response.data.quotes || [];
                const total = response.data.total || 0;
                // Página ficou além do fim (ex.: excluiu o último item da última página).
                if (!isMobileShell && rows.length === 0 && total > 0 && page > 0) {
                    setPage(Math.ceil(total / pageSize) - 1);
                    return;
                }
                if (isMobileShell && requestPage > 0) {
                    setQuotes((prev) => [...prev, ...rows.filter((row) => !prev.some((p) => p.service_quote_id === row.service_quote_id))]);
                } else {
                    setQuotes(rows);
                }
                setTotalCount(total);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Erro ao buscar orçamentos:', error);
                setQuotes([]);
                setTotalCount(0);
                setMessage('Erro ao buscar orçamentos');
                setSeverity('error');
                setOpenSnackbar(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [page, pageSize, appliedPeriod, selectedStatus, selectedCustomer, debouncedSearch, reloadKey, isMobileShell, mobilePage]);

    // Recarrega a página atual (converter em OS / excluir mudam status e contagem).
    const reloadCurrentPage = () => setReloadKey((k) => k + 1);

    const handleApplyFilters = () => {
        setAppliedPeriod({ start: selectedDate, end: selectedDateEnd });
        setPage(0);
        setMobilePage(0);
    };

    // Mobile: a folha "Filtros" aplica cliente + período de uma vez.
    const handleApplyMobileFilters = ({ customer, start, end }) => {
        setSelectedCustomer(customer);
        setSelectedDate(start);
        setSelectedDateEnd(end);
        setAppliedPeriod({ start, end });
        setPage(0);
        setMobilePage(0);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    };

    const getCustomers = async () => {
        try {
            const response = await httpClient.get('/customers');
            setCustomers(response.data);
        } catch (error) {
            console.error('Erro ao buscar clientes:', error);
        }
    }

    const handleChangeStatus = (status) => {
        setSelectedStatus(status);
        setPage(0);
        setMobilePage(0);
    }

    const handleChangeCustomer = (customerId) => {
        setSelectedCustomer(customerId);
        setPage(0);
        setMobilePage(0);
    }

    const handleTableSearch = (searchValue) => {
        setSearch(searchValue);
    };

    // Zera tudo, inclusive as datas (voltam a "todas as datas").
    const handleClearFilters = () => {
        setSelectedDate(null);
        setSelectedDateEnd(null);
        setSelectedStatus('');
        setSelectedCustomer('');
        setSearch('');
        setDebouncedSearch('');
        setAppliedPeriod({ start: null, end: null });
        setPage(0);
        setMobilePage(0);
    };

    // Chip "Período" removível: limpa o período selecionado e o aplicado.
    const handleClearPeriod = () => {
        setSelectedDate(null);
        setSelectedDateEnd(null);
        setAppliedPeriod({ start: null, end: null });
        setPage(0);
        setMobilePage(0);
    };

    const hasActiveFilters = Boolean(selectedStatus || selectedCustomer || search || selectedDate || selectedDateEnd);

    // "Operação do orçamento" (item 111 do FRONTEND-TASKS.md) já foi
    // implementada — "Visualizar"/clique na linha navega pra lá. A tela de
    // Visualização dedicada (item 112, `/servicequotes/:id/view`) ainda não
    // tem protótipo aprovado; quando ela existir, este link passa a apontar
    // pra ela (mesma evolução descrita em `prototipo-operacao-orcamento.html`).
    // Grava os filtros aplicados imediatamente antes de ir ao detalhe; o state
    // `fromList` faz o "Voltar" do detalhe usar o histórico (POP) e restaurá-los.
    const handleViewQuote = (quote) => {
        saveSnapshot({
            start: formatSnapshotDate(appliedPeriod.start),
            end: formatSnapshotDate(appliedPeriod.end),
            status: selectedStatus,
            customer: selectedCustomer,
            search: debouncedSearch,
            page,
            pageSize,
        });
        navigate(`/servicequotes/${quote.service_quote_id}/operator`, { state: { fromList: true } });
    };

    const handleDownloadPdf = async (quote) => {
        setPrintingQuoteId(quote.service_quote_id);
        try {
            const response = await httpClient.get(`/servicequotes/${quote.service_quote_id}/pdf`, {
                responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const urlBlob = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = urlBlob;
            link.download = `orcamento_servico_${quote.number}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlBlob);
        } catch (error) {
            console.error('Erro ao baixar PDF do orçamento:', error);
            setMessage('Erro ao baixar PDF do orçamento');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setPrintingQuoteId(null);
        }
    };

    const handleConvertToOS = (quote) => {
        setQuoteToConvert(quote);
        setOpenConvertDialog(true);
    }

    const confirmConvertToOS = async () => {
        if (!quoteToConvert) return;

        try {
            const response = await httpClient.post(`/servicequotes/${quoteToConvert.service_quote_id}/convert`);
            if (response.status === 200 || response.status === 201) {
                setMessage('Ordem de Serviço gerada com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                setOpenConvertDialog(false);
                setQuoteToConvert(null);
                // Redirecionar para a OS gerada; sem id na resposta, mantém o comportamento de recarregar
                if (response.data?.id) {
                    navigate(`/serviceorders/${response.data.id}/view`);
                } else {
                    reloadCurrentPage();
                }
            }
        } catch (error) {
            console.error('Erro ao converter orçamento:', error);
            setMessage('Erro ao gerar ordem a partir do orçamento: ' + (error.response?.data?.message || error.message));
            setSeverity('error');
            setOpenSnackbar(true);
        }
    }

    const handleDeleteQuote = (quote) => {
        setQuoteToDelete(quote);
        setOpenDeleteDialog(true);
    }

    const confirmDeleteQuote = async () => {
        if (!quoteToDelete) return;

        try {
            const response = await httpClient.delete(`/servicequotes/${quoteToDelete.service_quote_id}`);
            if (response.status === 200 || response.status === 204) {
                setMessage('Orçamento removido com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                setOpenDeleteDialog(false);
                setQuoteToDelete(null);
                reloadCurrentPage();
            }
        } catch (error) {
            console.error('Erro ao remover orçamento:', error);
            setMessage('Erro ao remover orçamento: ' + (error.response?.data?.message || error.message));
            setSeverity('error');
            setOpenSnackbar(true);
        }
    }

    useEffect(() => {
        getCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns = [
        {
            path: 'number',
            name: 'Número',
            invisible: false,
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                    {row.number}
                </Typography>
            ),
        },
        {
            path: 'created_at',
            name: 'Data',
            invisible: false,
            render: (row) => new Date(row.created_at).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }),
        },
        {
            path: 'customer_name',
            name: 'Cliente',
            invisible: false,
        },
        {
            path: 'status',
            name: 'Status',
            invisible: false,
            render: (row) => <StatusChip domain="serviceQuote" value={row.status} />,
        },
    ]

    const actions = [
        {
            icon: <Eye size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => handleViewQuote(row),
                tooltip: 'Visualizar',
                cursor: 'pointer',
                sx: { mr: 1 }
            }
        },
        ...(hasPermission('servicequote.update') ? [{
            icon: <Pencil size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => handleViewQuote(row),
                tooltip: 'Editar',
                cursor: 'pointer',
                sx: { mr: 1 }
            },
            ruleHide: ['ordem gerada']
        }] : []),
        {
            icon: (row) => printingQuoteId === row.service_quote_id ?
                <CircularProgress size={16} color="inherit" /> :
                <Download size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => handleDownloadPdf(row),
                tooltip: 'Imprimir / Baixar PDF',
                cursor: 'pointer',
                sx: { mr: 1 },
            }
        },
        ...(hasPermission('servicequote.send') ? [{
            icon: <Send size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => setQuoteToWhatsapp(row),
                tooltip: 'Enviar por WhatsApp',
                cursor: 'pointer',
                sx: { mr: 1 },
            },
        }] : []),
        ...(hasPermission('servicequote.update') ? [{
            icon: <ArrowLeftRight size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => handleConvertToOS(row),
                tooltip: 'Gerar Ordem',
                cursor: 'pointer',
                sx: { mr: 1 }
            },
            ruleShow: 'aprovado'
        }] : []),
        ...(hasPermission('servicequote.delete') ? [{
            icon: <Trash2 size={16} strokeWidth={2} color="#ef4444" />,
            props: {
                onClick: (row) => handleDeleteQuote(row),
                tooltip: 'Excluir',
                cursor: 'pointer',
                sx: { mr: 1 }
            },
            ruleHide: ['ordem gerada']
        }] : []),
    ]

    // Rótulo do chip fixo (`tb-field` — DESIGN-SYSTEM.md §5.5/§9: micro-label
    // uppercase renderizado fora do campo, não o `InputLabel`/floating label
    // padrão do MUI). Mesmo padrão de `service_order/List.js`/`sales/List.js`.
    const TbLabel = ({ children }) => (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );

    const TB_FIELD_RADIUS = '9px';

    const selectedCustomerName = customers.find(c => String(c.ID) === String(selectedCustomer))?.Name;

    // Shell mobile: lista em cartões do protótipo `orcamentos/lista.html` (mesmos dados, filtros e navegação).
    if (isMobileShell) {
        return (
            <PageScaffold
                links={[]}
                allowBleed
                titlePageTarget={'Orçamentos de Serviço'}
                setOpenSnackbar={setOpenSnackbar}
                setMessage={setMessage}
                openSnackbar={openSnackbar}
                severity={severity}
                message={message}
            >
                <MobileQuotesList
                    quotes={quotes}
                    loading={loading}
                    totalCount={totalCount}
                    hasMore={quotes.length < totalCount}
                    onLoadMore={() => setMobilePage((current) => current + 1)}
                    status={selectedStatus}
                    onStatusChange={handleChangeStatus}
                    search={search}
                    onSearchChange={handleTableSearch}
                    customers={customers}
                    customer={selectedCustomer}
                    appliedPeriod={appliedPeriod}
                    onApplyFilters={handleApplyMobileFilters}
                    canCreate={hasPermission('servicequote.create')}
                    onNew={() => navigate('/servicequotes/generate')}
                    onOpen={handleViewQuote}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget={'Orçamentos de Serviço'} setOpenSnackbar={setOpenSnackbar} setMessage={setMessage} openSnackbar={openSnackbar} severity={severity} message={message}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* Page head — ícone + título + descrição + ação primária,
                    igual a `prototipo-lista-orcamentos.html` (`page-head`/`page-icon`). */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <FileText size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Meus Orçamentos
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Acompanhe, gere ordens de serviço e gerencie os orçamentos emitidos.
                            </Typography>
                        </Box>
                    </Box>

                    {hasPermission('servicequote.create') && (
                        <NewItemButton label="Novo orçamento" onClick={() => navigate('/servicequotes/generate')} />
                    )}
                </Box>

                {/* Toolbar de filtros — Status/Cliente/Início/Fim/Busca
                    (§6.1 `report-toolbar`), igual à tela `view-lista` do
                    protótipo. */}
                <FilterToolbar
                    plain
                    activeCount={(selectedStatus ? 1 : 0) + (selectedCustomer ? 1 : 0) + (selectedDate || selectedDateEnd ? 1 : 0)}
                    onClear={handleClearFilters}
                    onApply={handleApplyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 220px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Número ou cliente…"
                                value={search}
                                onChange={(e) => handleTableSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            {hasActiveFilters && (
                                <Button
                                    variant="text"
                                    onClick={handleClearFilters}
                                    sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                                >
                                    Limpar
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={handleApplyFilters}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box sx={{ minWidth: 170 }}>
                            <TbLabel>Status</TbLabel>
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={selectedStatus}
                                    displayEmpty
                                    onChange={(e) => handleChangeStatus(e.target.value)}
                                    renderValue={(value) => serviceQuoteStatusLabel(value) || 'Todos'}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {STATUS_OPTIONS.map((statusOption) => (
                                        <MenuItem key={statusOption} value={statusOption}>
                                            {/* <StatusChip domain="serviceQuote" value={statusOption} sx={{ mr: 1 }} /> */}
                                            {serviceQuoteStatusLabel(statusOption)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ minWidth: 190 }} data-wide>
                            <TbLabel>Cliente</TbLabel>
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={selectedCustomer}
                                    displayEmpty
                                    onChange={(e) => handleChangeCustomer(e.target.value)}
                                    renderValue={() => selectedCustomerName || 'Todos'}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {customers.map((customer) => (
                                        <MenuItem key={customer.ID} value={customer.ID}>
                                            {customer.Name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <TbLabel>Período</TbLabel>
                            <DesktopPeriodSelector
                                start={selectedDate}
                                end={selectedDateEnd}
                                onChange={(start, end) => {
                                    setSelectedDate(start);
                                    setSelectedDateEnd(end);
                                }}
                                showShortcuts={false}
                                showRefresh={false}
                                align="start"
                                dualMonth
                                clearable
                            />
                        </Box>

                </FilterToolbar>

                {/* Chips de filtro ativo + contagem — mesma leitura do
                    `chips-row` do protótipo/`service_order/List.js`. */}
                <FilterChips>
                    {(appliedPeriod.start || appliedPeriod.end) && (
                        <Chip
                            label={`Período: ${appliedPeriod.start ? appliedPeriod.start.format('DD/MM/YYYY') : '…'} – ${appliedPeriod.end ? appliedPeriod.end.format('DD/MM/YYYY') : '…'}`}
                            size="small"
                            onDelete={handleClearPeriod}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {selectedStatus && (
                        <Chip
                            label={`Status: ${serviceQuoteStatusLabel(selectedStatus)}`}
                            size="small"
                            onDelete={() => handleChangeStatus('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {selectedCustomer && (
                        <Chip
                            label={`Cliente: ${selectedCustomerName || 'N/A'}`}
                            size="small"
                            onDelete={() => handleChangeCustomer('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {search && (
                        <Chip
                            label={`Busca: "${search}"`}
                            size="small"
                            onDelete={() => handleTableSearch('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${totalCount} orçamento${totalCount === 1 ? '' : 's'} encontrado${totalCount === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={quotes}
                    iconsActions={actions}
                    loading={loading}
                    onRowClick={(row) => handleViewQuote(row)}
                    rowPerPage={pageSize}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    itemLabel="orçamentos"
                    serverPagination={{
                        page,
                        rowsPerPage: pageSize,
                        total: totalCount,
                        onPageChange: setPage,
                        onRowsPerPageChange: handlePageSizeChange,
                    }}
                />
            </Box>
            <QuoteWhatsappDialog
                open={Boolean(quoteToWhatsapp)}
                quoteId={quoteToWhatsapp?.service_quote_id}
                quoteNumber={quoteToWhatsapp?.number}
                onClose={() => setQuoteToWhatsapp(null)}
                onSent={reloadCurrentPage}
                onResult={(msg, sev) => {
                    setMessage(msg);
                    setSeverity(sev);
                    setOpenSnackbar(true);
                }}
            />
            <DialogForm
                iconType={'info'}
                open={openConvertDialog}
                title={'Gerar Ordem de Serviço'}
                confirmButtonText={'Sim, Gerar'}
                content={'Tem certeza que deseja gerar uma Ordem de Serviço a partir deste orçamento?'}
                secondaryContent={'Esta ação criará uma nova Ordem de Serviço baseada neste orçamento.'}
                onSubmit={confirmConvertToOS}
                onClose={() => {
                    setOpenConvertDialog(false);
                    setQuoteToConvert(null);
                }}
            />
            <DialogForm
                iconType={'error'}
                open={openDeleteDialog}
                title={'Excluir Orçamento'}
                confirmButtonText={'Confirmar Exclusão'}
                content={'Tem certeza que deseja excluir este orçamento?'}
                secondaryContent={'Esta ação não pode ser desfeita.'}
                onSubmit={confirmDeleteQuote}
                onClose={() => {
                    setOpenDeleteDialog(false);
                    setQuoteToDelete(null);
                }}
            />
        </PageScaffold>
    )
}
